import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import {
  getClinicianPricing,
  getClinicianProfileById,
} from "../../../../api/services/api";
import {
  StyledDrawerContent,
  StyledDrawerHeader,
} from "../../../../components/DrawerTemplate";
import EmptyScreen from "../../../../components/EmptyScreen";
import Loader from "../../../../components/Loader";
import { RoleGuard } from "../../../../components/RBAC";
import { ClinicianGlobalUpdateForm } from "../../Pricing/ClinicianGlobalUpdateForm";
import { ServicePricingEditForm } from "../../Pricing/ServicePricingEditForm";

type ModeEditTarget = {
  serviceId: number;
  serviceName: string;
  mode: "AUDIO" | "VIDEO" | "HOME";
  config?: {
    id?: number;
    price: number;
    isActive: boolean;
  };
};

type ServiceUpdateTarget = {
  serviceId: number;
  serviceName: string;
};

function TherapistPricing() {
  const { id } = useParams();
  const clinicianId = Number(id);
  const [globalDrawerOpen, setGlobalDrawerOpen] = useState(false);
  const [serviceUpdate, setServiceUpdate] = useState<ServiceUpdateTarget | null>(
    null
  );
  const [modeEdit, setModeEdit] = useState<ModeEditTarget | null>(null);

  const { data: profileData, isLoading: profileLoading } = useQuery(
    ["clinician-profile", id],
    getClinicianProfileById,
    { enabled: Boolean(id) }
  );

  const { data: pricingData, isLoading: pricingLoading } = useQuery(
    ["clinician-pricing", id],
    getClinicianPricing,
    { enabled: Boolean(id) }
  );

  const clinicianName =
    profileData?.data?.users?.fullName ||
    profileData?.data?.users?.full_name ||
    pricingData?.data?.clinician?.fullName ||
    "Clinician";

  const servicePricing = pricingData?.data?.servicePricing || [];
  const summary = pricingData?.data?.summary;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-SA", {
      style: "currency",
      currency: "SAR",
    }).format(amount);

  if (profileLoading || pricingLoading) {
    return <Loader />;
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        gap={2}
        flexWrap="wrap"
      >
        <Box>
          <Typography variant="h4" mb={0.5}>
            Pricing
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage service pricing for {clinicianName}
            {summary?.totalPricingConfigs != null
              ? ` · ${summary.totalPricingConfigs} configs across ${summary.totalServices} services`
              : ""}
          </Typography>
        </Box>
        <RoleGuard allowedRoles={["SUPER_ADMIN", "ADMIN"]}>
          <Button variant="contained" onClick={() => setGlobalDrawerOpen(true)}>
            Update All Services
          </Button>
        </RoleGuard>
      </Box>

      {!servicePricing.length ? (
        <EmptyScreen type="pricing" />
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: "1px solid #eee" }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Service</TableCell>
                <TableCell>Audio</TableCell>
                <TableCell>Video</TableCell>
                <TableCell>Home</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {servicePricing.map((service: any) => (
                <TableRow key={service.serviceId}>
                  <TableCell>
                    <Typography fontWeight={600}>{service.serviceName}</Typography>
                    {service.serviceArabicName ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {service.serviceArabicName}
                      </Typography>
                    ) : null}
                  </TableCell>
                  {(["AUDIO", "VIDEO", "HOME"] as const).map((mode) => {
                    const config = service.pricing?.[mode];
                    return (
                      <TableCell key={mode}>
                        {config ? (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography>{formatCurrency(config.price)}</Typography>
                            <Chip
                              size="small"
                              label={config.isActive ? "Active" : "Inactive"}
                              color={config.isActive ? "success" : "default"}
                            />
                            <RoleGuard allowedRoles={["SUPER_ADMIN", "ADMIN"]}>
                              <IconButton
                                size="small"
                                aria-label={`Edit ${mode} price`}
                                onClick={() =>
                                  setModeEdit({
                                    serviceId: service.serviceId,
                                    serviceName: service.serviceName,
                                    mode,
                                    config: {
                                      id: config.id,
                                      price: config.price,
                                      isActive: config.isActive,
                                    },
                                  })
                                }
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </RoleGuard>
                          </Box>
                        ) : (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography color="text.secondary">—</Typography>
                            <RoleGuard allowedRoles={["SUPER_ADMIN", "ADMIN"]}>
                              <IconButton
                                size="small"
                                aria-label={`Add ${mode} price`}
                                onClick={() =>
                                  setModeEdit({
                                    serviceId: service.serviceId,
                                    serviceName: service.serviceName,
                                    mode,
                                  })
                                }
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </RoleGuard>
                          </Box>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell align="right">
                    <RoleGuard allowedRoles={["SUPER_ADMIN", "ADMIN"]}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setServiceUpdate({
                            serviceId: service.serviceId,
                            serviceName: service.serviceName,
                          })
                        }
                      >
                        Update Service
                      </Button>
                    </RoleGuard>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* All services */}
      <Drawer
        anchor="right"
        open={globalDrawerOpen}
        onClose={() => setGlobalDrawerOpen(false)}
      >
        <StyledDrawerHeader>
          <Typography>Update All Services</Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontFamily: "Inter_semibold",
              color: "black",
              opacity: "0.5",
              cursor: "pointer",
            }}
            onClick={() => setGlobalDrawerOpen(false)}
          >
            Cancel
          </Typography>
        </StyledDrawerHeader>
        <StyledDrawerContent>
          <ClinicianGlobalUpdateForm
            clinicianId={clinicianId}
            clinicianName={clinicianName}
            onClose={() => setGlobalDrawerOpen(false)}
          />
        </StyledDrawerContent>
      </Drawer>

      {/* One service */}
      <Drawer
        anchor="right"
        open={Boolean(serviceUpdate)}
        onClose={() => setServiceUpdate(null)}
      >
        <StyledDrawerHeader>
          <Typography>Update Service Pricing</Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontFamily: "Inter_semibold",
              color: "black",
              opacity: "0.5",
              cursor: "pointer",
            }}
            onClick={() => setServiceUpdate(null)}
          >
            Cancel
          </Typography>
        </StyledDrawerHeader>
        <StyledDrawerContent>
          {serviceUpdate ? (
            <ClinicianGlobalUpdateForm
              clinicianId={clinicianId}
              clinicianName={clinicianName}
              serviceIds={[serviceUpdate.serviceId]}
              serviceName={serviceUpdate.serviceName}
              onClose={() => setServiceUpdate(null)}
            />
          ) : null}
        </StyledDrawerContent>
      </Drawer>

      {/* Single mode create/edit */}
      <Drawer
        anchor="right"
        open={Boolean(modeEdit)}
        onClose={() => setModeEdit(null)}
      >
        <StyledDrawerHeader>
          <Typography>
            {modeEdit?.config?.id ? "Edit" : "Add"} {modeEdit?.mode} ·{" "}
            {modeEdit?.serviceName}
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontFamily: "Inter_semibold",
              color: "black",
              opacity: "0.5",
              cursor: "pointer",
            }}
            onClick={() => setModeEdit(null)}
          >
            Cancel
          </Typography>
        </StyledDrawerHeader>
        <StyledDrawerContent>
          {modeEdit ? (
            <ServicePricingEditForm
              clinicianId={clinicianId}
              serviceId={modeEdit.serviceId}
              consultationMode={modeEdit.mode}
              initialData={modeEdit.config}
              onClose={() => setModeEdit(null)}
            />
          ) : null}
        </StyledDrawerContent>
      </Drawer>
    </Box>
  );
}

export default TherapistPricing;
